import { lazy } from 'react';
import { ROLES_PORTAL } from './constants';

// ─── REGISTRO ÚNICO DE MÓDULOS ───────────────────────────────────────────────
// Cada pantalla del portal se declara UNA vez, aquí: quién puede entrar, con qué
// título se muestra y qué componente pinta. El menú (SECCIONES_MENU, abajo), el
// candado de App.js y el render salen de este registro. Antes eran cuatro
// lugares que había que mantener sincronizados a mano (ACCESO_MODULOS, la barra
// lateral, TITULOS_MODULO y la cadena de `modulo === …` del render) y ya se
// habían separado: el menú pedía permisos que el render no revisaba.
//
// Es la base del enrutador (areas/SIGUIENTE-SESION-navegacion.md): cada
// pantalla tiene su dirección web, y la ruta se declara aquí y en ningún otro
// lado. La dirección decide a qué pantalla QUISO ir el usuario; los candados de
// abajo deciden a cuál puede llegar.
//
// Cómo se lee una entrada:
//   ruta    → dirección de la pantalla, legible y en español, sin el prefijo
//             /portal (lo pone el enrutador). Única: modulos.test.js falla si
//             dos módulos comparten ruta. El id interno nunca cambia.
//   titulo  → título de la barra superior (si falta, la etiqueta del menú).
//   modulo  → capacidad de la clínica (`institucion_modulos`) que debe estar
//             encendida, para cualquier rol. "¿Esta clínica trabaja así?"
//   acceso  → roles que pueden entrar. `{}` = basta el rol; `{ permiso }` =
//             además el usuario debe tener esa llave del catálogo de permisos.
//             "¿Este usuario puede?"
//   vista   → (ctx) => elemento. `ctx` es el estado de App que las pantallas
//             reciben como props. null = la pinta App.js por fuera del registro
//             (el flujo de nueva orden y la consulta, que no se desmonta).
//   conId   → (opcional) la pantalla también abre UN registro por dirección:
//             `ruta + '/' + id` (etapa 3). Su valor es el formato del id, una
//             clave de FORMATOS_ID (abajo): 'uuid' o 'correlativo'. La URL lleva
//             solo ese id, nunca nombre, DPI ni datos del paciente, y no da
//             acceso: la pantalla lee el registro con la RLS de siempre. La
//             pantalla recibe `ctx.registroId` (y `ctx.registroInvalido`) y abre/cierra con
//             `ctx.abrirRegistro(id)` / `ctx.cerrarRegistro()`.
//   rutasViejas → (opcional) direcciones que tuvo antes la pantalla. Siguen
//             llegando a ella (enlaces y PWA guardados) y App.js las cambia por
//             `ruta` con replace. Cuentan para rutasDuplicadas.
//   rutaRegistro → (opcional, con conId) prefijo de la dirección del registro
//             cuando no es la ruta del módulo: el CRM vive en /crm y la ficha
//             de una cuenta en /crm/cuentas/:id. Cerrar vuelve a `ruta`.
//
// Un módulo que no esté aquí no se pinta para nadie: nace cerrado, igual que una
// tabla nueva en la base. La RLS sigue siendo la barrera real de los datos; esto
// solo impide que una pantalla que no le toca al usuario alcance a dibujarse.

// ── Carga diferida de pantallas ──────────────────────────────────────────────
// Cada pantalla viaja en su propio archivo y se descarga al abrirla, no al
// entrar. Un médico no se baja el laboratorio entero (caja, catálogo, precios,
// comisiones) para ver sus resultados: ese código nunca llega a su navegador.
const TabRegistros = lazy(() => import('./tabs/TabRegistros'));
const TabUsuarios = lazy(() => import('./tabs/TabUsuarios'));
const TabQBResultados = lazy(() => import('./tabs/TabQBResultados'));
const TabIngresarResultados = lazy(() => import('./tabs/TabIngresarResultados'));
const TabDirectorioMedicos = lazy(() => import('./tabs/TabDirectorioMedicos'));
const TabCatalogo = lazy(() => import('./tabs/TabCatalogo'));
const TabCotizaciones = lazy(() => import('./tabs/TabCotizaciones'));
const TabContrasenas = lazy(() => import('./tabs/TabContrasenas'));
const TabCaja = lazy(() => import('./tabs/TabCaja'));
const TabDashboard = lazy(() => import('./tabs/TabDashboard'));
const TabComparativas = lazy(() => import('./tabs/TabComparativas'));
const TabFinGastos = lazy(() => import('./tabs/TabFinGastos'));
const TabFinResultados = lazy(() => import('./tabs/TabFinResultados'));
const TabInventarioLab = lazy(() => import('./tabs/TabInventarioLab'));
const TabConfiguracion = lazy(() => import('./tabs/TabConfiguracion'));
const TabMedicoDashboard = lazy(() => import('./tabs/TabMedicoDashboard'));
const TabExpedienteMedico = lazy(() => import('./tabs/TabExpedienteMedico'));
const TabCiclosFertilidad = lazy(() => import('./tabs/TabCiclosFertilidad'));
const TabLaboratorioFIV = lazy(() => import('./tabs/TabLaboratorioFIV'));
const TabPlannerMedico = lazy(() => import('./tabs/TabPlannerMedico'));
const TabAgendaConfig = lazy(() => import('./tabs/TabAgendaConfig'));
const TabAdmision = lazy(() => import('./tabs/TabAdmision'));
const TabOrganizaciones = lazy(() => import('./tabs/TabOrganizaciones'));
const TabHomeMedico = lazy(() => import('./tabs/TabHomeMedico'));
const TabMisArchivosMedico = lazy(() => import('./tabs/TabMisArchivosMedico'));
const TabImagenesMedico = lazy(() => import('./tabs/TabImagenesMedico'));
const TabEnviarImagenes = lazy(() => import('./tabs/TabEnviarImagenes'));
const TabSecretariaInicio = lazy(() => import('./tabs/TabSecretariaInicio'));
const TabGerenciaInicio = lazy(() => import('./tabs/TabGerenciaInicio'));
const TabGerenciaDescuentos = lazy(() => import('./tabs/TabGerenciaDescuentos'));
const TabGerenciaBitacora = lazy(() => import('./tabs/TabGerenciaBitacora'));
const TabComercial = lazy(() => import('./tabs/TabComercial'));
const TabPases = lazy(() => import('./tabs/TabPases'));
const TabGerenciaCobertura = lazy(() => import('./tabs/TabGerenciaCobertura'));
const TabArchivosSecretaria = lazy(() => import('./tabs/TabArchivosSecretaria'));
// Caja de pacientes ≠ TabCaja: aquélla es el corte diario de órdenes del
// laboratorio; ésta son las cuentas por cobrar de procedimientos que el paciente
// paga en abonos. Comparten la palabra y nada más.
const TabCajaPacientes = lazy(() => import('./tabs/TabCajaPacientes'));
const TabFarmacia = lazy(() => import('./tabs/TabFarmacia'));
const TabGerenciaCaja = lazy(() => import('./tabs/TabGerenciaCaja'));
const TabGerenciaAgenda = lazy(() => import('./tabs/TabGerenciaAgenda'));
// Agenda general de recepción ≠ TabGerenciaAgenda: aquélla es la del manager y
// oculta la identidad del paciente salvo permiso expreso; ésta trae el detalle
// completo, porque recepción es quien habla con el paciente.
const TabAgendaInstitucion = lazy(() => import('./tabs/TabAgendaInstitucion'));
const TabOrdenes = lazy(() => import('./tabs/TabOrdenes'));

const STAFF    = { admin: {}, qb: {}, op: {} };
const ADMIN_QB = { admin: {}, qb: {} };

// Planificador 1 a 1: el único que carga horarios y bloqueos, y por tanto el
// único donde se agenda de verdad.
const planificador = (c, extra = {}) => (
  <TabPlannerMedico currentUser={c.user} nuevaCitaTrigger={c.nuevaCitaTrigger} onNuevaCitaConsumida={() => c.setNuevaCitaTrigger(0)} {...extra} />
);

export const MODULOS = {
  // ── Portal externo · médico ──
  'medico-clinica': {
    ruta: '/clinica',
    titulo: 'Mi Clínica',
    acceso: { medico: {}, secretaria: {}, manager: { permiso: 'gerencia.agenda.ver' } },
    // La secretaria y el manager entran directo al calendario: TabHomeMedico es
    // el home del médico (resultados, KPIs del laboratorio) y ellos no tienen
    // acceso a esos datos — les devolvería tarjetas vacías o errores.
    vista: (c) => c.isMedico
      ? <TabHomeMedico currentUser={c.user} onNavigate={c.navegar} nuevaCitaTrigger={c.nuevaCitaTrigger} renderCalendario={(opts = {}) => planificador(c, { compacto: !!opts.compacto })} />
      : planificador(c),
  },
  'medico-agenda-config': {
    ruta: '/horarios',
    titulo: 'Horario y bloqueos',
    acceso: { medico: {}, secretaria: {}, manager: { permiso: 'gerencia.horarios' } },
    // Horarios y bloqueos son configuración de la agenda, no citas: se gobiernan
    // con sus propias llaves de gerencia, por eso entran como props y no se
    // deducen de `puede_agendar`.
    vista: (c) => (
      <TabAgendaConfig
        puedeEditarHorarios={c.isManager && c.permisos['gerencia.horarios'] === true}
        puedeEditarBloqueos={c.isManager && c.permisos['gerencia.bloqueos'] === true}
      />
    ),
  },
  'medico-expediente': {
    ruta: '/pacientes',
    conId: 'uuid',                 // /pacientes/:id → expediente (med_pacientes.id)
    titulo: 'Mis Pacientes',
    acceso: { medico: {} },
    vista: (c) => (
      <TabExpedienteMedico
        currentUser={c.user} onOpenWorkspace={c.onOpenWorkspace}
        pacienteIdUrl={c.registroId} pacienteIdInvalido={c.registroInvalido}
        onAbrirPaciente={c.abrirRegistro} onCerrarPaciente={c.cerrarRegistro}
      />
    ),
  },
  // La consulta NO se pinta desde aquí: App.js la mantiene montada (escondida)
  // aunque el médico se mueva por el menú, para no perder borrador ni dictado.
  'medico-workspace': {
    ruta: '/consulta',
    conId: 'uuid',                 // /consulta/:id → consulta de ese med_paciente
    titulo: 'Consulta',
    acceso: { medico: {} },
    vista: null,
  },
  'medico-ciclos': {
    ruta: '/ciclos',
    titulo: 'Ciclos',
    modulo: 'fertilidad.ciclos',
    acceso: { medico: {} },
    vista: (c) => <TabCiclosFertilidad onAbrirExpediente={c.onOpenWorkspace} />,
  },
  'medico-dashboard': {
    ruta: '/mis-resultados',
    conId: 'correlativo',          // /mis-resultados/:corr → detalle del resultado (RLS: solo sus órdenes)
    titulo: 'Resultados',
    acceso: { medico: {} },
    vista: (c) => <TabMedicoDashboard records={c.records} results={c.results} currentUser={c.user} onOpenWorkspace={c.onOpenWorkspace}
      registroId={c.registroId} registroInvalido={c.registroInvalido} abrirRegistro={c.abrirRegistro} cerrarRegistro={c.cerrarRegistro} rutaOrden={(id) => rutaDe('medico-dashboard', id)} />,
  },
  'medico-imagenes': {
    ruta: '/ultrasonidos',
    titulo: 'Ultrasonidos',
    acceso: { medico: {} },
    vista: (c) => <TabImagenesMedico currentUser={c.user} onOpenWorkspace={c.onOpenWorkspace} />,
  },
  'medico-archivos': {
    ruta: '/mis-archivos',
    titulo: 'Mis Archivos',
    acceso: { medico: {} },
    vista: () => <TabMisArchivosMedico />,
  },
  'admision': {
    ruta: '/admision',
    modulo: 'admision.formulario',
    acceso: { medico: {}, secretaria: {}, manager: {} },
    vista: () => <TabAdmision />,
  },

  // ── Portal externo · secretaría ──
  'secretaria-inicio': {
    ruta: '/inicio',
    acceso: { secretaria: {} },
    vista: (c) => <TabSecretariaInicio currentUser={c.user} onNavigate={c.navegar} />,
  },
  'secretaria-archivos': {
    ruta: '/archivos',
    acceso: { secretaria: {} },
    vista: (c) => <TabArchivosSecretaria permisos={c.permisos} />,
  },
  'secretaria-agenda-institucion': {
    ruta: '/agenda-general',
    titulo: 'Agenda general de la institución',
    acceso: { secretaria: { permiso: 'agenda.institucion.ver' } },
    // «Abrir en Agenda» del detalle lleva al planificador 1 a 1.
    vista: (c) => <TabAgendaInstitucion permisos={c.permisos} onIrAAgendar={() => c.navegar('medico-clinica')} />,
  },
  'caja-pacientes': {
    ruta: '/caja-pacientes',
    titulo: 'Caja de pacientes',
    // Viene apagada de fábrica: la enciende el manager por secretaria. No todas
    // cobran, y la que cobra responde por un arqueo.
    acceso: { secretaria: { permiso: 'caja.ver' } },
    vista: (c) => <TabCajaPacientes currentUser={c.user} permisos={c.permisos} />,
  },
  'farmacia-catalogo': {
    ruta: '/farmacia',
    titulo: 'Farmacia',
    modulo: 'farmacia.catalogo',
    acceso: { secretaria: { permiso: 'farmacia.catalogo.ver' }, manager: { permiso: 'farmacia.catalogo.ver' } },
    vista: (c) => <TabFarmacia currentUser={c.user} permisos={c.permisos} />,
  },

  // ── Portal externo · embrióloga ──
  'lab-fiv-hojas': {
    ruta: '/fiv/hojas',
    titulo: 'Laboratorio FIV',
    modulo: 'fertilidad.laboratorio',
    acceso: { embriologa: {} },
    vista: () => <TabLaboratorioFIV vista="hojas" />,
  },
  'lab-fiv-inventario': {
    ruta: '/fiv/inventario',
    titulo: 'Inventario de criopreservación',
    modulo: 'fertilidad.laboratorio',
    acceso: { embriologa: {} },
    vista: () => <TabLaboratorioFIV vista="inventario" />,
  },
  'lab-fiv-control': {
    ruta: '/fiv/control',
    titulo: 'Formato Control',
    modulo: 'fertilidad.laboratorio',
    acceso: { embriologa: {} },
    vista: () => <TabLaboratorioFIV vista="control" />,
  },

  // ── Portal externo · gerencia ──
  // Cada pantalla además revalida su permiso contra la base: si la llave falta,
  // el RPC rebota y se muestra el error.
  'gerencia-inicio': {
    ruta: '/indicadores',
    titulo: 'Indicadores',
    acceso: { manager: { permiso: 'gerencia.dashboard.ver' } },
    vista: (c) => <TabGerenciaInicio currentUser={c.user} permisos={c.permisos} onNavigate={c.navegar} />,
  },
  'gerencia-agenda': {
    ruta: '/agenda-organizacion',
    titulo: 'Agenda de la organización',
    acceso: { manager: { permiso: 'gerencia.agenda.ver' } },
    vista: (c) => <TabGerenciaAgenda permisos={c.permisos} />,
  },
  'gerencia-caja': {
    ruta: '/caja-organizacion',
    titulo: 'Caja de la organización',
    acceso: { manager: { permiso: 'gerencia.caja.ver' } },
    vista: (c) => <TabGerenciaCaja currentUser={c.user} permisos={c.permisos} />,
  },
  'gerencia-cobertura': {
    ruta: '/cobertura',
    titulo: 'Grupos de cobertura',
    acceso: { manager: { permiso: 'gerencia.cobertura' } },
    vista: (c) => <TabGerenciaCobertura permisos={c.permisos} />,
  },
  'gerencia-descuentos': {
    ruta: '/descuentos',
    titulo: 'Descuentos',
    acceso: { manager: { permiso: 'gerencia.finanzas.descuentos' } },
    vista: (c) => <TabGerenciaDescuentos currentUser={c.user} permisos={c.permisos} />,
  },
  'gerencia-bitacora': {
    ruta: '/bitacora',
    titulo: 'Bitácora',
    acceso: { manager: { permiso: 'gerencia.auditoria.ver' } },
    vista: () => <TabGerenciaBitacora />,
  },
  // Organizaciones = el mundo externo (instituciones, sus médicos y su personal).
  // El manager la ve como «Mi institución».
  'organizaciones': {
    ruta: '/organizaciones',
    titulo: 'Organizaciones',
    acceso: { admin: {}, manager: {} },
    vista: (c) => <TabOrganizaciones isAdmin={c.isAdmin} />,
  },

  // ── Operaciones · laboratorio ──
  // Nueva orden la pinta App.js: es un formulario por pasos con su propia barra.
  'nueva-orden': {
    ruta: '/nueva-orden',
    acceso: STAFF,
    vista: null,
  },
  'registros': {
    ruta: '/registros',
    conId: 'correlativo',          // /registros/:corr → fila desplegada
    titulo: 'Registros / Historial',
    acceso: STAFF,
    vista: (c) => <TabRegistros records={c.records} setRecords={c.setRecords} results={c.results} isAdmin={c.isAdmin}
      registroId={c.registroId} registroInvalido={c.registroInvalido} abrirRegistro={c.abrirRegistro} cerrarRegistro={c.cerrarRegistro} rutaOrden={(id) => rutaDe('registros', id)} />,
  },
  'preordenes': {
    ruta: '/pases',
    titulo: 'Preórdenes',
    acceso: STAFF,
    vista: (c) => <TabPases onCrearOrden={c.precargarOrdenDesdeSolicitud} sharedCatalog={c.sharedCatalog} currentUser={c.user} setQuotations={c.setQuotations} />,
  },
  'ing-resultados': {
    ruta: '/ingreso-resultados',
    conId: 'correlativo',          // /ingreso-resultados/:corr → formulario (con guardia de salida)
    titulo: 'Ingreso de Resultados',
    acceso: STAFF,
    vista: (c) => <TabIngresarResultados records={c.records} results={c.results} setResults={c.setResults} setRecords={c.setRecords} currentUser={c.user} isAdmin={c.isAdmin} isQB={c.isQB}
      registroId={c.registroId} registroInvalido={c.registroInvalido} abrirRegistro={c.abrirRegistro} cerrarRegistro={c.cerrarRegistro} rutaOrden={(id) => rutaDe('ing-resultados', id)} />,
  },
  // «Órdenes» (bloque B, 02-nov-2026): tablero de las órdenes en curso por etapa,
  // con tiempo en la etapa y atrasadas. Solo lee lo que ya está en memoria.
  // /qb/ordenes (enlaces y PWA guardados) llega aquí: ver `rutasViejas`.
  'qb-ordenes': {
    ruta: '/ordenes',
    rutasViejas: ['/qb/ordenes'],
    conId: 'correlativo',          // /ordenes/:corr → detalle de la orden
    titulo: 'Órdenes',
    acceso: STAFF,
    vista: (c) => <TabOrdenes records={c.records} results={c.results} distributionLogs={c.distributionLogs} user={c.user} permisos={c.permisos}
      navegar={c.navegar} registroId={c.registroId} registroInvalido={c.registroInvalido} abrirRegistro={c.abrirRegistro} cerrarRegistro={c.cerrarRegistro} />,
  },
  'qb-resultados': {
    ruta: '/qb/resultados',
    conId: 'correlativo',          // /qb/resultados/:corr → panel de revisión
    titulo: 'Resultados',
    acceso: STAFF,
    vista: (c) => <TabQBResultados records={c.records} results={c.results} setResults={c.setResults} currentUser={c.user} isAdmin={c.isAdmin} isQB={c.isQB}
      registroId={c.registroId} registroInvalido={c.registroInvalido} abrirRegistro={c.abrirRegistro} cerrarRegistro={c.cerrarRegistro} rutaOrden={(id) => rutaDe('qb-resultados', id)} />,
  },
  // Resultados completados: solo lectura + descarga. No está en el menú.
  'resultados-aprobados': {
    ruta: '/resultados-completados',
    conId: 'correlativo',          // /resultados-completados/:corr → panel (solo lectura)
    titulo: 'Resultados Completados',
    acceso: STAFF,
    vista: (c) => (
      <TabQBResultados
        records={c.records}
        results={c.results}
        setResults={c.setResults}
        currentUser={c.user}
        isAdmin={c.isAdmin}
        isQB={c.isQB}
        soloAprobados={true}
        distributionLogs={c.distributionLogs}
        setDistributionLogs={c.setDistributionLogs}
        registroId={c.registroId} registroInvalido={c.registroInvalido} abrirRegistro={c.abrirRegistro} cerrarRegistro={c.cerrarRegistro}
        rutaOrden={(id) => rutaDe('resultados-aprobados', id)}
      />
    ),
  },
  'lab-imagenes': {
    ruta: '/enviar-imagenes',
    titulo: 'Enviar Imágenes',
    acceso: STAFF,
    vista: (c) => <TabEnviarImagenes doctors={c.doctors} currentUser={c.user} />,
  },
  'medicos': {
    ruta: '/directorio-medicos',
    titulo: 'Directorio de Médicos',
    acceso: STAFF,
    vista: (c) => <TabDirectorioMedicos doctors={c.doctors} setDoctors={c.setDoctors} records={c.records} isAdmin={c.isAdmin} user={c.user} />,
  },

  // ── Operaciones · finanzas, análisis y sistema ──
  'cotizaciones': {
    ruta: '/cotizaciones',
    titulo: 'Cotizaciones',
    acceso: STAFF,
    vista: (c) => <TabCotizaciones quotations={c.quotations} setQuotations={c.setQuotations} sharedCatalog={c.sharedCatalog} currentUser={c.user} isAdmin={c.isAdmin} />,
  },
  'contrasenas': {
    ruta: '/contrasenas',
    titulo: 'Contraseñas',
    acceso: STAFF,
    vista: (c) => <TabContrasenas passwords={c.passwords} setPasswords={c.setPasswords} corrContrasena={c.corrContrasena} setCorrContrasena={c.guardarCorrContrasena} isAdmin={c.isAdmin} currentUser={c.user} imprimirContrasena={c.imprimirContrasena} />,
  },
  'caja': {
    ruta: '/caja',
    titulo: 'Caja',
    acceso: STAFF,
    vista: (c) => <TabCaja records={c.records} passwords={c.passwords} isAdmin={c.isAdmin} isQB={c.isQB} currentUser={c.user} setRecords={c.setRecords} />,
  },
  'catalogo': {
    ruta: '/catalogo',
    titulo: 'Catálogo y Precios',
    acceso: ADMIN_QB,
    vista: (c) => <TabCatalogo isAdmin={c.isAdmin} isQB={c.isQB} sharedCatalog={c.sharedCatalog} setSharedCatalog={c.setSharedCatalog} sharedCatOrder={c.sharedCatOrder} setSharedCatOrder={c.setSharedCatOrder} />,
  },
  'dashboard': {
    ruta: '/dashboard',
    titulo: 'Dashboard',
    acceso: ADMIN_QB,
    vista: (c) => <TabDashboard records={c.records} results={c.results} doctors={c.doctors} />,
  },
  'comparativas': {
    ruta: '/comparativas',
    titulo: 'Comparativas',
    acceso: ADMIN_QB,
    vista: (c) => <TabComparativas records={c.records} />,
  },
  // Estado de resultados: solo socios. Admin siempre (tiene_permiso devuelve
  // true para admin); QB solo con la llave, hoy concedida únicamente a Isa.
  'fin-resultados': {
    ruta: '/finanzas/estado-de-resultados',
    titulo: 'Estado de resultados',
    acceso: { admin: {}, qb: { permiso: 'finanzas.resultados' }, socio: {} },
    vista: (c) => <TabFinResultados records={c.records} isAdmin={c.isAdmin} esSocio={c.user?.role === 'socio'} />,
  },
  'fin-gastos': {
    ruta: '/finanzas/gastos',
    titulo: 'Gastos',
    acceso: ADMIN_QB,
    vista: (c) => <TabFinGastos currentUser={c.user} isAdmin={c.isAdmin} />,
  },
  'fin-inventario': {
    ruta: '/finanzas/inventario',
    titulo: 'Inventario y compras',
    acceso: ADMIN_QB,
    vista: () => <TabInventarioLab />,
  },
  // Solo la vista Registrar; admin y qb usan fin-inventario.
  'inv-registro': {
    ruta: '/inventario',
    titulo: 'Inventario',
    acceso: { op: {} },
    vista: () => <TabInventarioLab soloRegistro />,
  },
  // CRM: "Comercial" no es un rol, es la llave `crm.comercial` sobre QB u
  // operador. Admin la tiene siempre.
  'comercial': {
    ruta: '/crm',
    conId: 'uuid',                 // /crm/cuentas/:id → ficha de la cuenta (crm_cuentas.id)
    rutaRegistro: '/crm/cuentas',
    acceso: { admin: {}, qb: { permiso: 'crm.comercial' }, op: { permiso: 'crm.comercial' } },
    vista: (c) => (
      <TabComercial currentUser={c.user}
        registroId={c.registroId} registroInvalido={c.registroInvalido} abrirRegistro={c.abrirRegistro} cerrarRegistro={c.cerrarRegistro}
        rutaCuenta={(id) => rutaDe('comercial', id)} />
    ),
  },
  'usuarios': {
    ruta: '/usuarios',
    titulo: 'Usuarios del laboratorio',
    acceso: { admin: {} },
    vista: (c) => <TabUsuarios currentUser={c.user} isAdmin={c.isAdmin} />,
  },
  'configuracion': {
    ruta: '/configuracion',
    titulo: 'Configuración',
    acceso: ADMIN_QB,
    vista: (c) => <TabConfiguracion corrContrasena={c.corrContrasena} setCorrContrasena={c.guardarCorrContrasena} corr={c.corr} setCorr={c.handleSetCorr} isAdmin={c.isAdmin} currentUser={c.user} />,
  },
};

// ─── DIRECCIONES ─────────────────────────────────────────────────────────────
// Puente URL ↔ módulo. La dirección no da acceso: solo dice a dónde quiso ir el
// usuario; App.js pasa ese deseo por `puedeEntrar` antes de pintar nada.

// Rutas repetidas (deberían ser cero). La usan modulos.test.js y el chequeo de
// desarrollo de abajo.
export function rutasDuplicadas(registro = MODULOS) {
  const vistas = new Map();
  const repetidas = [];
  for (const [id, m] of Object.entries(registro)) {
    for (const r of [m.ruta || '', ...(m.rutasViejas || [])].map(x => x.toLowerCase())) {
      if (vistas.has(r)) repetidas.push(`${r} (${vistas.get(r)} y ${id})`);
      else vistas.set(r, id);
    }
  }
  return repetidas;
}

const MODULO_POR_RUTA = new Map(Object.entries(MODULOS).map(([id, m]) => [m.ruta.toLowerCase(), id]));
const MODULO_POR_RUTA_VIEJA = new Map(Object.entries(MODULOS).flatMap(([id, m]) => (m.rutasViejas || []).map(r => [r.toLowerCase(), id])));
// Prefijo de `prefijo/:id` → módulo, solo para módulos con conId.
const prefijoRegistro = (m) => (m.rutaRegistro || m.ruta);
const MODULO_POR_PREFIJO = new Map(Object.entries(MODULOS).filter(([, m]) => m.conId).map(([id, m]) => [prefijoRegistro(m).toLowerCase(), id]));

// ── Direcciones con identificador (etapa 3) ─────────────────────────────────
// Formato que debe tener el id de cada `conId`. Un id que no cumple no se le
// pasa a la pantalla como id: la pantalla muestra «sin acceso» sin consultar.
export const FORMATOS_ID = {
  uuid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  // Formatos reales de records.correlativo (revisado el 31-oct-2026): '0001'…
  // (nextCorr: serie opcional + 4 dígitos, p. ej. 'A0001') y los de muestra
  // 'DEMO-F01'. Un UUID u otra cosa no es un correlativo.
  correlativo: /^(?:[A-Za-z]{1,6}-)?[A-Za-z]?\d{1,6}$/,
};

// Dirección del módulo (sin el prefijo /portal). Un id desconocido va a "/",
// que el enrutador resuelve al módulo inicial del usuario. Con `registroId`
// (solo módulos `conId`) va a la dirección de ese registro.
export function rutaDe(id, registroId = null) {
  const m = MODULOS[id];
  if (!m) return '/';
  if (registroId != null && registroId !== '' && m.conId) {
    return `${prefijoRegistro(m)}/${encodeURIComponent(String(registroId))}`;
  }
  return m.ruta;
}

const normalizar = (pathname) => (pathname || '/').replace(/\/+$/, '') || '/';

// Lee una dirección: { modulo, registroId, idInvalido }. modulo = null si no es
// de ningún módulo ("/" o desconocida). `registroId` solo si la dirección es
// `ruta/:id` de un módulo `conId` y el id tiene el formato declarado; si no lo
// tiene, `idInvalido` = true (la pantalla muestra «sin acceso»). Una ruta vieja
// (`rutasViejas`) es de su módulo y trae `vieja: true` para que App.js la cambie.
export function leerRuta(pathname) {
  const p = normalizar(pathname);
  const exacto = MODULO_POR_RUTA.get(p.toLowerCase());
  if (exacto) return { modulo: exacto, registroId: null, idInvalido: false };
  const vieja = MODULO_POR_RUTA_VIEJA.get(p.toLowerCase());
  if (vieja) return { modulo: vieja, registroId: null, idInvalido: false, vieja: true };
  const corte = p.lastIndexOf('/');
  if (corte <= 0) return { modulo: null, registroId: null, idInvalido: false };
  const padre = MODULO_POR_PREFIJO.get(p.slice(0, corte).toLowerCase());
  const formato = padre && FORMATOS_ID[MODULOS[padre].conId];
  if (!formato) return { modulo: null, registroId: null, idInvalido: false };
  let crudo = p.slice(corte + 1);
  try { crudo = decodeURIComponent(crudo); } catch { return { modulo: padre, registroId: null, idInvalido: true }; }
  if (!formato.test(crudo)) return { modulo: padre, registroId: null, idInvalido: true };
  const registroId = MODULOS[padre].conId === 'uuid' ? crudo.toLowerCase() : crudo;
  return { modulo: padre, registroId, idInvalido: false };
}

// Módulo que pide una dirección, o null si no corresponde a ninguno ("/" o una
// ruta desconocida). Tolera la barra final y mayúsculas. `ruta/:id` de un
// módulo `conId` también es de ese módulo.
export function moduloDeRuta(pathname) {
  return leerRuta(pathname).modulo;
}

// En desarrollo, una ruta repetida se avisa al cargar (además del test).
if (process.env.NODE_ENV === 'development') {
  const rep = rutasDuplicadas();
  if (rep.length) console.error('[modulos] Rutas repetidas en el registro:', rep.join(' · '));
  const sinRuta = Object.entries(MODULOS).filter(([, m]) => !/^\/[a-z0-9/-]+$/.test(m.ruta || '')).map(([id]) => id);
  if (sinRuta.length) console.error('[modulos] Módulos sin ruta válida:', sinRuta.join(', '));
  const conIdMalo = Object.entries(MODULOS).filter(([, m]) => m.conId && !FORMATOS_ID[m.conId]).map(([id]) => id);
  if (conIdMalo.length) console.error('[modulos] conId sin formato conocido:', conIdMalo.join(', '));
}

// ─── ¿PUEDE ENTRAR? ──────────────────────────────────────────────────────────
// Los tres candados en un solo lugar: rol, capacidad de la clínica y permiso del
// usuario. Lo usan el menú (qué se ofrece) y App.js (qué se pinta), así que ya
// no pueden discrepar. Se exige `=== true` a propósito: si falló la lectura de
// permisos o módulos, la puerta queda cerrada.
export function puedeEntrar(id, user, modulos, permisos) {
  const m = MODULOS[id];
  const regla = m && user ? m.acceso[user.role] : null;
  if (!regla) return false;
  if (m.modulo && modulos?.[m.modulo] !== true) return false;
  if (regla.permiso && permisos?.[regla.permiso] !== true) return false;
  return true;
}

// ─── MENÚ ────────────────────────────────────────────────────────────────────
// Solo dice DÓNDE aparece cada pantalla y con qué etiqueta. Quién la ve lo
// decide `puedeEntrar`, con las reglas del registro de arriba. Una misma
// pantalla puede estar en varias secciones con otro nombre («Mi Clínica» para el
// médico, «Agenda» para la secretaria, «Agendas» para el manager).
//
// `roles` de la sección decide a quién se le muestra: '*' = todo el personal del
// laboratorio (admin/qb/op), nunca los portales.
// OJO: el id de sección debe ser único; repetirlo duplica las keys de React y el
// menú se pinta en bucle (bug 17-oct-2026).
export const SECCIONES_MENU = [
  // ── Médico ──
  {
    id: 'medico',
    label: 'Médico',
    roles: ['medico'],
    items: [
      { id:'medico-clinica',       label:'Mi Clínica',         icon:'calendario',  sub:'Agenda y resumen' },
      { id:'medico-agenda-config', label:'Horario y bloqueos', icon:'horario',     sub:'Franjas de atención y ausencias' },
      { id:'admision',             label:'Admisión',           icon:'formulario',  sub:'Hojas de datos que llenan los pacientes' },
      { id:'medico-expediente',    label:'Mis Pacientes',      icon:'pacientes',   sub:'Expedientes' },
      { id:'medico-ciclos',        label:'Ciclos',             icon:'ciclos',      sub:'Hojas de tratamiento de fertilidad' },
      { id:'medico-dashboard',     label:'Resultados',         icon:'tubo',        sub:'Recibidos del laboratorio' },
      { id:'medico-imagenes',      label:'Ultrasonidos',       icon:'ultrasonido', sub:'Estudios recibidos' },
      { id:'medico-archivos',      label:'Mis Archivos',       icon:'carpeta',     sub:'Mi biblioteca de material' },
    ],
  },

  // ── Embrióloga (Fertility · Reprolab) ──
  // Personal de la clínica, no del laboratorio. Trabaja sobre los ciclos que el
  // médico ya abrió: no crea ciclos ni ve agenda, caja ni recetas.
  {
    id: 'embriologa',
    label: 'Laboratorio FIV',
    roles: ['embriologa'],
    items: [
      { id:'lab-fiv-hojas',      label:'Hojas TVA / FET', icon:'microscopio', sub:'Ciclos abiertos y sus hojas' },
      { id:'lab-fiv-inventario', label:'Inventario crio', icon:'crio',        sub:'Tanques, canisters y cryotops' },
      { id:'lab-fiv-control',    label:'Formato Control', icon:'grafico',     sub:'Una fila por ciclo e indicadores' },
    ],
  },

  // ── Secretaria ──
  // Nada de expedientes ni datos clínicos: la RLS ya se lo impide, y mostrarle
  // accesos que van a rebotar solo genera confusión.
  {
    id: 'secretaria',
    label: 'Agenda',
    roles: ['secretaria'],
    items: [
      { id:'secretaria-inicio', label:'Inicio', icon:'inicio', sub:'Tu día de un vistazo' },
      // Dos vistas de la misma agenda, y ninguna sobra: «Agenda» es un médico a
      // la vez, que es donde se agenda de verdad; «Agenda general» son todos los
      // médicos de la institución juntos, que es lo que necesita quien contesta
      // el teléfono y no sabe de antemano con qué doctor va el paciente.
      { id:'medico-clinica',                label:'Agenda',             icon:'calendario',    sub:'Citas de mis doctores' },
      { id:'secretaria-agenda-institucion', label:'Agenda general',     icon:'agendaGeneral', sub:'Toda la institución en una vista' },
      { id:'medico-agenda-config',          label:'Horario y bloqueos', icon:'horario',       sub:'Franjas de atención y ausencias' },
      { id:'admision',                      label:'Admisión',           icon:'formulario',    sub:'Hojas de datos que llenan los pacientes' },
      // Archivos es pantalla propia y no una pestaña del expediente: la
      // secretaria no tiene expediente. Llega al paciente por la agenda.
      { id:'secretaria-archivos',           label:'Archivos',           icon:'carpeta',       sub:'Documentos, resultados e informes del paciente' },
      { id:'caja-pacientes',                label:'Caja',               icon:'caja',          sub:'Cuentas y abonos de pacientes' },
      // Farmacia: la atiende recepción. Solo en clínicas con el módulo encendido.
      { id:'farmacia-catalogo',             label:'Farmacia',           icon:'farmacia',      sub:'Existencias, catálogo y precios' },
    ],
  },

  // ── Manager de institución ──
  // Administra su organización: personal, indicadores y facturación. Sobre las
  // agendas de sus médicos puede consultar, agendar y reprogramar; cancelar y
  // borrar no — eso queda en recepción y en el propio doctor.
  {
    id: 'manager',
    label: 'Mi institución',
    roles: ['manager'],
    items: [
      { id:'gerencia-inicio',      label:'Inicio',              icon:'inicio',        sub:'Indicadores de tu organización' },
      { id:'organizaciones',       label:'Mi institución',      icon:'instituciones', sub:'Personal, médicos y facturación' },
      // Dos vistas de lo mismo, y ninguna sobra: «Agendas» es un médico a la vez
      // (es donde se agenda de verdad); «Agenda general» son todos juntos.
      { id:'gerencia-agenda',      label:'Agenda general',      icon:'agendaGeneral', sub:'Todos los médicos a la vez' },
      { id:'medico-clinica',       label:'Agendas',             icon:'calendario',    sub:'Calendario médico por médico' },
      { id:'medico-agenda-config', label:'Horarios y bloqueos', icon:'horario',       sub:'Franjas de atención y ausencias' },
      { id:'gerencia-caja',        label:'Caja',                icon:'caja',          sub:'Cobrado, cortes y cartera' },
      { id:'admision',             label:'Admisión',            icon:'formulario',    sub:'Hojas de datos de los pacientes' },
      { id:'gerencia-cobertura',   label:'Cobertura',           icon:'cobertura',     sub:'Quiénes comparten expediente' },
      { id:'gerencia-descuentos',  label:'Descuentos',          icon:'etiqueta',      sub:'Autorizaciones pendientes' },
      { id:'gerencia-bitacora',    label:'Bitácora',            icon:'bitacora',      sub:'Quién cambió qué' },
      { id:'farmacia-catalogo',    label:'Farmacia',            icon:'farmacia',      sub:'Existencias, catálogo y precios' },
    ],
  },

  // ── QB (admin y qb) ──
  {
    id: 'qb',
    label: 'QB',
    roles: ['admin', 'qb'],
    items: [
      { id:'qb-ordenes',    label:'Órdenes',    icon:'qbOrdenes' },
      { id:'qb-resultados', label:'Resultados', icon:'qbResultados', badge:'pending_results' },
    ],
  },
  // ── Operador ──
  {
    id: 'op',
    label: 'Operador',
    roles: ['op'],
    items: [
      { id:'qb-ordenes',    label:'Órdenes',    icon:'qbOrdenes' },
      { id:'qb-resultados', label:'Resultados', icon:'qbResultados', badge:'approved_results' },
      { id:'inv-registro',  label:'Inventario', icon:'tubo', sub:'Entradas, salidas y conteo físico' },
    ],
  },
  // ── Laboratorio ──
  {
    id: 'laboratorio',
    label: 'Laboratorio',
    roles: ['*'],
    items: [
      { id:'nueva-orden',    label:'Nueva orden',           icon:'nuevaOrden' },
      { id:'preordenes',     label:'Pases',                 icon:'preordenes' },
      { id:'registros',      label:'Registros',             icon:'registros',         badge:'total_records' },
      { id:'ing-resultados', label:'Ingreso de resultados', icon:'ingresoResultados', badge:'rejected_results' },
      { id:'lab-imagenes',   label:'Enviar imágenes',       icon:'imagenes' },
      { id:'medicos',        label:'Directorio médicos',    icon:'medicos' },
    ],
  },
  // ── Socio (2-nov-2026) ──
  // Inversionista: solo informes financieros ya sumados. La base
  // (`socio_finanzas`) no le entrega pacientes, órdenes, médicos ni facturas.
  {
    id: 'socio',
    label: 'Informes',
    roles: ['socio'],
    items: [
      { id:'fin-resultados', label:'Estado de resultados', icon:'grafico', sub:'Ingresos, gastos y utilidad' },
    ],
  },
  // ── Finanzas ──
  {
    id: 'finanzas',
    label: 'Finanzas',
    roles: ['*'],
    items: [
      { id:'cotizaciones', label:'Cotizaciones', icon:'cotizaciones' },
      { id:'contrasenas',  label:'Contraseñas',  icon:'contrasenas' },
      { id:'caja',         label:'Caja',         icon:'caja' },
    ],
  },
  // ── Análisis ──
  {
    id: 'analisis',
    label: 'Análisis',
    roles: ['admin', 'qb'],
    items: [
      { id:'dashboard',    label:'Dashboard',    icon:'dashboard' },
      { id:'comparativas', label:'Comparativas', icon:'comparativas' },
    ],
  },
  // ── Gestión financiera ──
  // Gastos, proveedores, equipos y estado de resultados. Solo admin y QB: la RLS
  // de las tablas fin_* tampoco deja entrar a nadie más.
  {
    id: 'gestion-financiera',
    label: 'Gestión financiera',
    roles: ['admin', 'qb'],
    items: [
      { id:'fin-resultados', label:'Estado de resultados', icon:'grafico', sub:'Utilidad bruta, operativa y neta' },
      { id:'fin-gastos',     label:'Gastos',               icon:'recibo',  sub:'Facturas, proveedores y por pagar' },
      { id:'fin-inventario', label:'Inventario y compras', icon:'tubo',    sub:'Qué comprar, vencimientos y faltantes' },
    ],
  },
  // ── Comercial (CRM) ──
  {
    id: 'comercial',
    label: 'Comercial',
    roles: ['admin', 'qb', 'op'],
    items: [
      { id:'comercial', label:'CRM', icon:'comercial' },
    ],
  },
  // ── Catálogo ──
  {
    id: 'catalogo',
    label: 'Catálogo',
    roles: ['admin', 'qb'],
    items: [
      { id:'catalogo', label:'Exámenes', icon:'examenes', badge_label:'propio' },
    ],
  },
  // ── Sistema ──
  // Organizaciones = el mundo externo (instituciones, sus médicos y su
  // personal). Usuarios = staff interno del laboratorio. La división sigue la
  // frontera de confianza a propósito: evita darle un rol interno a personal de
  // un cliente.
  {
    id: 'sistema',
    label: 'Sistema',
    roles: ['admin', 'qb'],
    items: [
      { id:'organizaciones', label:'Organizaciones', icon:'instituciones', badge_label:'propio' },
      { id:'usuarios',       label:'Usuarios',       icon:'usuarios',      badge_label:'propio' },
      { id:'configuracion',  label:'Configuración',  icon:'configuracion', badge_label:'propio' },
    ],
  },
];

export function seccionVisible(seccion, role) {
  if (!role) return false;
  // '*' es el personal del laboratorio: los portales nunca entran por ahí.
  if (seccion.roles.includes('*')) return !ROLES_PORTAL.includes(role);
  return seccion.roles.includes(role);
}

// Secciones del menú con sus entradas ya filtradas para este usuario. Las
// secciones que se quedan sin entradas no se devuelven.
export function menuPara(user, modulos, permisos) {
  return SECCIONES_MENU
    .filter(sec => seccionVisible(sec, user?.role))
    .map(sec => ({ section: sec, visibleItems: sec.items.filter(it => puedeEntrar(it.id, user, modulos, permisos)) }))
    .filter(x => x.visibleItems.length);
}

// Primera aparición de un módulo en el menú (etiqueta e ícono del título).
export function entradaMenu(id) {
  for (const sec of SECCIONES_MENU) {
    const it = sec.items.find(i => i.id === id);
    if (it) return it;
  }
  return null;
}

// ─── ATERRIZAJE ──────────────────────────────────────────────────────────────
// Dónde entra cada rol. Si la pantalla preferida no le está permitida (un
// manager sin la llave de indicadores, por ejemplo), cae en la primera del menú
// que sí lo esté. Se resuelve en el render, no en un efecto: un efecto corre
// DESPUÉS del primer pintado, y ese pintado alcanzaba a mostrarle el portal de
// operaciones a quien no le corresponde.
// Sin ninguna pantalla permitida → cadena vacía → no se pinta ningún módulo.
export const MODULO_INICIAL = {
  admin:      'registros',
  qb:         'registros',
  op:         'registros',
  medico:     'medico-clinica',
  secretaria: 'secretaria-inicio',
  manager:    'gerencia-inicio',
  embriologa: 'lab-fiv-hojas',
  socio:      'fin-resultados',
};

export function moduloInicial(user, modulos, permisos) {
  const preferido = MODULO_INICIAL[user?.role];
  if (preferido && puedeEntrar(preferido, user, modulos, permisos)) return preferido;
  const primera = menuPara(user, modulos, permisos)[0];
  return primera ? primera.visibleItems[0].id : '';
}
